import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getUserList, grantPermission, revokePermission } from "../../api";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackBarSlice";
import { hideLoader, showLoader } from "../../store/loaderSlice";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  contractId: string;
  contractName: string;
}

interface User {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_shared?: boolean;
}

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  contractId,
  contractName,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | "">("");
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getUserList("", 1, 100);
      if (response && response.data) {
        setUsers(response.data);
      } else {
        dispatch(
          showSnackbar({
            message: "Failed to load users",
            severity: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      dispatch(
        showSnackbar({
          message: "Failed to load users",
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (selectedUser === "") {
      dispatch(
        showSnackbar({
          message: "Please select a user to share with",
          severity: "warning",
        })
      );
      return;
    }

    dispatch(showLoader());
    try {
      await grantPermission(selectedUser as number, parseInt(contractId));

      dispatch(
        showSnackbar({
          message: "Contract shared successfully",
          severity: "success",
        })
      );
      onClose();
    } catch (error) {
      console.error("Error sharing contract:", error);
      dispatch(
        showSnackbar({
          message: "Failed to share contract",
          severity: "error",
        })
      );
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleUserChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedUser(event.target.value as number | "");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Share Contract</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body1" gutterBottom>
            Share <strong>{contractName}</strong> with another user
          </Typography>
        </Box>
        <Box sx={{ minHeight: 120 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={30} />
            </Box>
          ) : users && users.length > 0 ? (
            <FormControl fullWidth variant="outlined">
              <InputLabel id="user-select-label">Select User</InputLabel>
              <Select
                labelId="user-select-label"
                id="user-select"
                value={selectedUser}
                onChange={handleUserChange}
                label="Select User"
              >
                <MenuItem value="">
                  <em>Select a user</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.user_id} value={user.user_id}>
                    {`${user.first_name} ${user.last_name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              No users found
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleShare}
          variant="contained"
          color="primary"
          disabled={selectedUser === ""}
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareModal;
